import { request } from './request';

// 数据资产·UGC 分享中心 (2026-09-01, doc/数据资产/对接设计.md):
// 数据由后端同步器从美服社区库 (xtool-community.process_paras) 定时拉取,
// 页面只读展示 + 手动触发同步. 本模块对应后端 /v1/assets/ugc-shares.

export interface UgcShare {
    id: number;
    uuid: string;
    createdAt: number;
    updatedAt: number;
    /** 源 process_paras.id, 即 CMS "参数ID" */
    sourceId: number;
    sourceType?: string;
    sourceRefId?: string;
    creatorId: number;
    creatorName: string;
    materialIdRaw?: number;
    /** 耗材名 (=标题) */
    materialName: string;
    origMaterialName?: string;
    brand: string;
    paraName?: string;
    thickness?: number | null;
    color: string;
    description?: string;
    categoryCode: string;
    categoryCodeL2: string;
    aiCategoryL1?: string;
    aiCategoryL2?: string;
    /** 源机型 code (如 JS002) */
    deviceCode: string;
    /** 归一展示名 (如 M2), 未匹配为空 */
    deviceName: string;
    /** 归一到本平台 device.device_key, 未匹配为空 */
    deviceKey: string;
    laserPowerCode: string;
    laserSourceCode: string;
    machineHeadCode?: string;
    processingModeCode: string;
    processingTypeCode: string;
    /** 源内容状态 published/down/draft/blocked/deleted */
    ugcStatus: string;
    /** 准入审核 ("是否通过") approved/pending/rejected, 空=未入审核池 */
    auditStatus: string;
    reviewType?: string;
    qualityLabel: string;
    evaluationSource: string;
    evaluationReason?: string;
    rewardAuditState?: string;
    isFeatured: boolean;
    opTags?: string;
    coverImage: string;
    paraCover?: string;
    ogImage?: string;
    /** JSON string: 效果图 URL 数组 */
    effectImages?: string;
    /** JSON string: 参数快照 (功率/速度/次数等全量) */
    paraSnapshot: string;
    matrixInfo?: string;
    processingTips?: string;
    sourceFile?: string;
    /** "有用" (worked) 行为计数 */
    workedCount: number;
    /** "没用" (unworked) 行为计数 */
    unworkedCount: number;
    /** JSON string: 全量行为计数 {view, save_in_studio, ...} */
    actionStats?: string;
    /** 加工次数 = actionStats 的 save_in_studio + save_in_xcs (后端现算) */
    processCount: number;
    publishedAt: number;
    editedAt?: number;
    sourceUpdatedAt?: number;
    sourceSyncedAt: number;
    /**
     * AI 还原的"加工前耗材图" —— 视觉模型把 coverImage 那张效果图里的加工痕迹抹掉、
     * 反推出耗材加工前的样子, 与 coverImage 成对。空 = 这条还没有还原图。
     * ⚠ 推测图不是实拍, 做训练/标注素材时必须与实拍区分。
     */
    restoredImageUrl?: string;
}

export interface UgcListParams {
    keyword?: string;
    deviceCode?: string;
    categoryCode?: string;
    categoryCodeL2?: string;
    processingMode?: string;
    processingType?: string;
    auditStatus?: string;
    /** published(默认)/down/deleted/blocked/all */
    ugcStatus?: string;
    qualityLabel?: string;
    featured?: string;
    /** yes=worked>0 / no=unworked>0 */
    hasWorked?: string;
    /** yes=有耗材还原图(加工前) / no=只有加工后效果图 */
    restored?: string;
    page?: number;
    pageSize?: number;
}

export interface UgcListResult {
    list: UgcShare[];
    total: number;
    page: number;
    pageSize: number;
}

export interface UgcSyncState {
    sourcePlatform: string;
    dataset: string;
    /** idle / running / failed */
    status: string;
    triggerBy: string;
    lastRunAt: number;
    lastOkAt: number;
    durationMs: number;
    failStreak: number;
    lastError: string;
    /** JSON string: {fetched, inserted, updated, unchanged, marked, total} */
    stats: string;
}

export interface StatusCount {
    key: string;
    count: number;
}

export interface UgcStats {
    byUgcStatus: StatusCount[];
    byAuditStatus: StatusCount[];
    workedSum: number;
    unworkedSum: number;
    /** 有耗材还原图(加工前)的条数, 口径同列表默认: 只数 ugc_status=published 的行 */
    restoredCount?: number;
    devices: { deviceCode: string; deviceName: string; n: number }[];
    categories: { categoryCode: string; categoryCodeL2: string; n: number }[];
    syncState?: UgcSyncState;
}

const BASE = '/v1/assets/ugc-shares';

export function listUgcShares(params: UgcListParams): Promise<UgcListResult> {
    return request<UgcListResult>(BASE, { query: params });
}

export function getUgcShare(id: number): Promise<UgcShare> {
    return request<UgcShare>(`${BASE}/${id}`);
}

export function getUgcStats(): Promise<UgcStats> {
    return request<UgcStats>(`${BASE}/stats`);
}

export function getUgcSyncState(): Promise<UgcSyncState | null> {
    return request<UgcSyncState | null>(`${BASE}/sync-state`);
}

export function triggerUgcSync(): Promise<{ started: boolean }> {
    return request<{ started: boolean }>(`${BASE}/sync`, { method: 'POST' });
}

// ============ 耗材识别中心 / 加工数据中心 (2026-09-02) ============
// 这两个中心**不做数据同步**, 后端实时代理 prism-service-api, 响应即 prism 原始
// 结构 (snake_case), 与原平台 studio-admin-console 消费同一套字段, 不做二次映射.

/** 识别任务列表行 (prism dashboard/recognition/tasks). */
export interface PrismRecogRow {
    task_id: string;
    uid: string | null;
    material_name: string;
    category: string;
    device_model: string;
    power: number | string | null;
    is_official: boolean;
    confidence: number | null;
    /** good / bad / none */
    rating: string;
    status: string;
    created_at: string;
    image_url: string;
    thumb_url: string;
    description: string;
    processing_count: number;
    processing_defects: string[];
    /** 最近一次加工的胜出质量 (未加工 / 未打标为空串) */
    processing_quality: string;
    /** 该识别名下所有加工存档的场景标签去重集合 (一图可多标) */
    processing_scenes: string[];
}

/** 识别汇总 (prism dashboard/recognition/summary). */
export interface PrismRecogSummary {
    kpi: {
        total: number; completed: number; avg_confidence: number;
        good: number; bad: number; none: number;
        good_rate: number; official_count: number; official_ratio: number;
    };
    by_device: { device: string; good: number; bad: number; none: number; total: number }[];
    by_category: { category: string; count: number; avg_confidence: number }[];
    rating_breakdown: { good: number; bad: number; none: number };
    official_vs_community?: Record<string, { count: number; good: number; bad: number }>;
}

/** 加工汇总 (prism dashboard/processing/summary). */
export interface PrismProcSummary {
    /**
     * 转化漏斗 —— 三级单位统一为**识别任务数** (2026-09-02):
     *   recognized                 识别任务总数;
     *   selected                   用户从识别结果里选定了材料的任务数;
     *   processed_from_recognition  选定的材料在该次识别之后真的被加工过的任务数.
     * 另外两个是**全量加工口径**(不限识别链路), 给「加工强度」和各图表当分母:
     *   processed / process_events 全部加工归档的去重材料数 / 加工次数.
     */
    funnel?: {
        recognized: number; selected: number; processed_from_recognition: number;
        processed: number; process_events: number;
        /**
         * 其中**带材料标识**的加工次数 (源库大量存档的 material_id 是空串/'0' 哨兵)。
         * 与 processed 同一 population —— 「加工强度」只能用这一对, 用 process_events
         * 当分子会虚高好几倍 (实测 1408/46 = 30.6, 正确是 607/46 = 13.2)。
         */
        process_events_identified?: number;
        /**
         * 选定级按**本平台材料主数据**拆档 (2026-09-09): official 选的材料能关联到平台材料
         * (候选耗材编码优先, 退回用户点选的旧库 id), custom 两条线索都对不上 —— 用户自己在
         * Studio 存的材料。processed_from_recognition_official 是被加工那级里能关联上的部分。
         */
        selected_official?: number; selected_custom?: number;
        processed_from_recognition_official?: number;
    };
    /**
     * 漏斗统计口径 (2026-09-08, 对齐原型的「对齐窗口 / 全部时间」开关):
     * archives_since 全库第一条加工归档的日期 (不随筛选变), aligned 本次是否按对齐口径,
     * funnel_from 漏斗实际起点。加工归档是后上线的能力, 识别早得多 —— 不对齐时那批
     * "上线前根本没法被加工"的识别混在分母里, 后几级转化率被结构性稀释。
     */
    window?: { archives_since: string; aligned: boolean; funnel_from: string; date_to: string };
    adoption?: {
        adopted: number; adjusted: number; no_recommendation: number; compared: number;
        scanned: number; unresolved: number; adoption_rate: number; truncated: boolean;
    };
    /**
     * 效果分档 —— **两套口径同列并存** (prism 迁移 0048 起, 2026-10-08 接入; 后端见
     * material-library assetprism/quality.go): v1 best/good/acceptable/poor (配 score/defects) 与
     * v2 加工质量评估 perfect/minor_flaw/abnormal/unjudgable (配 indicators/unable_reason), 八个键一起返回、
     * 各数各的。页面上一律用 parts.tsx 的 qualityTierCounts 归并, 别只加其中一套 —— 另一套会凭空消失且不报错。
     * top_indicators 是 v2 命中最多的核心指标 (中文名), 与 top_defects (v1 缺陷码) 并列。
     */
    effect?: {
        best: number; good: number; acceptable: number; poor: number;
        perfect?: number; minor_flaw?: number; abnormal?: number; unjudgable?: number;
        labeled: number; unlabeled: number; low_confidence: number; scanned: number;
        truncated: boolean; top_defects?: { code: string; count: number }[];
        top_indicators?: { code: string; count: number }[];
        /**
         * official 子档 (原型首屏卡「官方材料 · 官方参数」, 2026-09-09 定口径):
         * 官方参数 = 原样采用推荐参数 (adopted); 官方材料 = 归档材料对得上平台主数据, **或**
         * 它归因到的识别任务的主候选耗材编码能解析到平台材料 —— 识别选定的材料存在 XCS 用户
         * 素材表, 归档挂的是那个雪花 id, 只按 legacy_mid 判这一档恒为 0。
         * scanned / unlabeled 说明这一档的总量与未打标数 (分母为什么小)。
         */
        official?: {
            best: number; good: number; acceptable: number; poor: number;
            perfect?: number; minor_flaw?: number; abnormal?: number; unjudgable?: number;
            scanned: number; unlabeled: number;
        };
    };
    by_device?: {
        device: string; process_count: number; materials: number; labeled: number;
        best: number; good: number; acceptable: number; poor: number; low_confidence: number;
        perfect?: number; minor_flaw?: number; abnormal?: number; unjudgable?: number;
    }[];
    by_category?: { category: string; materials: number; process_count: number }[];
    /** 被加工材料按来源三分类 (走过识别 > 官方材料 > 用户自定义), 与加工明细「来源」列同一判定。 */
    by_source?: { source: string; materials: number; process_count: number }[];
    top_materials?: {
        material_id: string; material_name: string; process_count: number;
        last_processed_at: string; source_kind?: string;
    }[];
    /**
     * 加工场景分布 (2026-09-16)。源 = prism `archive_scene_labels` 的增量打标。
     *
     * **三个分母各不相同**, 所以只给计数不给占比 (见 SceneCard.tsx):
     * `tags` ÷ `evaluable` 且一图可多标、`unable_reasons` ÷ `unevaluable`、
     * 「不可评估」本身 ÷ `labeled`。
     *
     * `unlabeled` 拆出 `no_image`: 前者混着**积压**(打标追上就归零)与**无图**
     * (入队要求 image_url 非空 ⇒ 永远追不上), `unlabeled - no_image` 才是打标欠的账。
     */
    scene?: {
        total: number; labeled: number; unlabeled: number; no_image: number;
        evaluable: number; unevaluable: number;
        tags: { code: string; count: number }[];
        unable_reasons: { code: string; count: number }[];
    };
}

/** 材料维度聚合行 (prism dashboard/processing/materials). */
export interface PrismMaterialRow {
    material_id: string;
    material_name: string;
    /** 一级类别, 取本平台材料主数据 material.type1; 用户自定义材料为空 */
    category: string;
    /** 来源三分类: recognized=走过识别链路 / official=本平台官方材料 / user_custom=用户自定义 / unknown */
    source_kind: 'recognized' | 'official' | 'user_custom' | 'unknown' | '';
    device_model: string;
    image_url: string;
    /** 加工前底图 (XCS 画布底图, 开工前相机拍的工作台/材料照); 源库没存的为空串 */
    pre_image_url: string;
    process_count: number;
    latest_archive_id: number;
    latest_archived_at: string;
    latest_verdict: string;
    latest_effect: string | null;
}

/** 加工存档行/详情 (prism dashboard/processing/record(s)). */
/** 数据资产行上关联到的本平台材料主数据 (后端 matref 解析; 解析不到为 null). */
export interface MaterialRef {
    xcode: string; uuid: string; legacyMid: number;
    name: { zh?: string; en?: string };
    type1: string; type2: string; thickness: number; color: string; colorHex: string;
    status: string; materialKind: string; domain: string; image: string;
}
/** official=对上本平台主数据 / user_custom=有线索但本平台没有 (用户自定义材料) / unknown=无线索 */
export type MaterialSource = 'official' | 'user_custom' | 'unknown';
/** 识别行能拿到的全部材料线索 (候选编码优先, 退回用户点选的旧库 id). */
export interface MaterialClues {
    materialCode: string; skuCode: string; selectedMaterialId: string; selectedName: string; isOfficial: boolean | null;
}

export interface PrismArchive {
    archive_id: number;
    material_id: string;
    material?: MaterialRef | null;
    material_source?: MaterialSource;
    material_name: string;
    /** 一级类别, 取本平台材料主数据 material.type1; 用户自定义材料为空 */
    category: string;
    /** 来源三分类, 与材料聚合行同一口径 */
    source_kind: 'recognized' | 'official' | 'user_custom' | 'unknown' | '';
    /** 上报这次加工的用户 (源库 user_id / user_email); 线上约一成存档没带, 为空串 */
    user_id: string;
    user_email: string;
    /** 上报单号 (源库 report_id); 极少数存档没有, 为空串 */
    report_id: string;
    /**
     * 这个材料 × 这个用户 的加工次数 (当前筛选口径, 与材料聚合行的 process_count 同范围).
     * 匿名上报 (无 user_id) 给 0 —— 源库里彼此无法区分, 不能归成同一个人.
     */
    user_process_count: number;
    device_model: string;
    source: string;
    image_url: string;
    /** 加工前底图 (archive.background_image_url); 约一成存档没有, 为空串 */
    pre_image_url: string;
    archived_at: string;
    recognition?: { task_id: string; recognized_at: string; candidate_rank: string } | null;
    recommended?: Record<string, unknown> | null;
    /**
     * 平台推荐参数 (2026-09-09): 存档判定为官方材料时, 后端按 材料 xcode + 机型 + 工艺 + 加工模式
     * 回查本平台参数库的推荐行 —— 识别没给候选时"推荐值"就靠它填.
     * modeMatched=false 表示该材料下没有同模式的参数行, 返回的是别的模式的行, 只能参考;
     * ambiguous=true 表示该机型有多个功率档 (存档只记到机型, 不猜是哪档), rows 全给出来.
     */
    platform_recommended?: {
        xcode: string; deviceName: string; archCode: string; subProcess: string;
        modeValue: string; modeMatched: boolean; ambiguous: boolean;
        rows: { deviceKey: string; subProcess: string; modeGroup: string; modeValue: string; params: Record<string, unknown> | null; paramRowId: number }[];
    } | null;
    actual?: Record<string, unknown> | null;
    verdict: string;
    diff?: string[] | null;
    /**
     * 胜出效果标签 (人工优先、再取最新, 不看口径)。quality 落在哪个集合就是哪套口径 (parts.tsx isV2Quality):
     * v1 配 score/defects (v2 行恒 null/[]), v2 配 indicators (命中的核心指标中文名) / unable_reason (仅 unjudgable 有值)。
     */
    effect?: {
        quality?: string; score?: number; defects?: string[]; confidence?: number;
        indicators?: string[]; unable_reason?: string | null;
        description?: string; model?: string; source?: string;
        labeled_by?: string | number | null; labeled_at?: string;
    } | null;
    ai_effect?: unknown;
    corrections?: unknown;
    /**
     * 源库是否已有 v2 两列 (prism 迁移 0048), 仅详情接口返回。false 时 v2 复核会被后端拒绝,
     * 详情页退回 v1 四档按钮; 缺省 (老后端) 按 true 处理。
     */
    quality_v2_ready?: boolean;
    /**
     * 这一次加工的胜出场景标签 (prism `archive_scene_labels`, 人工优先)。
     * 未打标为 null;`evaluable=false` 的行没有标签、只有 `unable_reason`（非成品照等）。
     */
    scene?: { tags: string[]; evaluable: boolean; unable_reason: string } | null;
    /**
     * 用户满意度 (prism `material_archive_ratings`, 按 report_id 关联, 取最新一条评价)。
     * 未评价为 null —— Studio 1.9 前的上报恒为 null, 之后也只有约 1% 的加工有评价。
     * reasons 是不满意原因 code (客户端词表), note 是用户自由填写的备注。
     */
    satisfaction?: Satisfaction | null;
}

/** 用户满意度 (prism 加工存档的第二段上报, Studio 1.9 起). */
export interface Satisfaction { satisfied: boolean; reasons: string[]; note: string; rated_at: string }

export interface PrismPage<T> { data: T[]; total: number; page: number; page_size: number }

/** 识别列表筛选 (prism 支持的全集; 无关键词搜索 —— prism 列表接口不提供该参数). */
export interface RecogListParams {
    device?: string; official?: string; rating?: string; category?: string; uid?: string;
    date_from?: string; date_to?: string;
    /** true/false —— 识别后是否有加工 */
    has_processing?: string;
    /** true/false —— 用户有没有在识别结果里选定材料 (漏斗第二级那批) */
    has_selection?: string;
    /** official / user_custom —— 选定的材料有没有关联到本平台材料主数据 (隐含 has_selection=true) */
    linked?: string;
    /** 加工场景标签 (逗号分隔多值), 命中任一即可 */
    processing_scene?: string;
    processing_quality?: string;
    processing_defect?: string;
    processing_verdict?: string;
    page?: number; pageSize?: number;
}

export interface ProcListParams {
    /** 用户 UID 或邮箱, 子串匹配 */
    uid?: string;
    /** 材料关键词: 本平台材料名/xcode/旧库 id, 或源库原始 material_id */
    /** 归档号 (archive_id) 精确匹配一次加工上报 */
    archive_id?: string;
    material_q?: string;
    /** 效果打标状态 true=已打标 / false=待打标 */
    labeled?: string;
    /** 结果图有无 true=有 / false=无 */
    has_result_image?: string;
    /** 用户满意度 true=满意 / false=不满意 / none=未评价 (同一次上报评过多次按最新一条) */
    satisfied?: string;
    device?: string; quality?: string; defect?: string; has_defect?: string;
    /** v2 核心指标中文名 (逗号分隔多值), 命中任一即可; 与 defect 是两套口径下的同一件事 */
    indicator?: string;
    /** 来源三分类 recognized / official / user_custom */
    source?: string;
    /** 本平台一级类别 (material.type1) */
    category?: string;
    verdict?: string; date_from?: string; date_to?: string;
    /** 加工场景标签 (逗号分隔多值), 命中任一即可 */
    scene?: string;
    /** true = 只看能归因到识别任务的加工 (取代旧的 ai_only) */
    recognized_only?: string;
    material_id?: string; task_id?: string;
    /** 明细按加工时间排序: asc 旧→新; 缺省 / desc 新→旧 (仅读本库时后端认) */
    order?: 'asc' | 'desc';
    page?: number; pageSize?: number;
}

export function listRecognitions(params: RecogListParams) {
    return request<PrismPage<PrismRecogRow>>('/v1/assets/recognitions', { query: params });
}

/** aligned=true 走「对齐窗口」口径 (漏斗只统计第一条加工归档之后的识别), 见 PrismProcSummary.window。 */
export function getRecognitionStats(params: Omit<RecogListParams, 'page' | 'pageSize'> & { aligned?: string } = {}) {
    return request<{
        summary: PrismRecogSummary;
        processingSummary?: PrismProcSummary;
        /**
         * 场景分布的**识别链路口径** (只统计能归因到识别任务的加工存档) ——
         * 与 processingSummary.scene 的全量口径是两个数, 各自的卡上都写了口径, 别互相验算。
         */
        scene?: PrismProcSummary['scene'];
        /** 当前筛选里加工侧接口不支持的维度 (口径警告条) */
        unsupportedFilters: string[];
    }>('/v1/assets/recognitions/stats', { query: params });
}

export function getRecognition(taskId: string) {
    return request<{
        detail: {
            task_id: string; status: string; device_model: string; power: number | string;
            image_url: string; image_urls?: string[]; description?: string; created_at: string;
            user_selection?: Record<string, unknown> | null;
            material?: MaterialRef | null; material_source?: MaterialSource; material_clues?: MaterialClues;
            result?: {
                candidates?: unknown[];
                recognition_status?: string;
                params_availability?: string;
            };
        };
        feedback?: unknown[];
        processing?: PrismPage<PrismArchive>;
    }>(`/v1/assets/recognitions/${encodeURIComponent(taskId)}`);
}

// ============ 矩阵识别中心 (2026-10-09) ============
// 与耗材识别同样直连 prism (matrix_tasks), 口径对齐 studio-admin-console「矩阵识别」页.

/** 矩阵最佳格 (result.quality_evaluation.best_position, 旧存量在 result 顶层). */
export interface MatrixBestPosition {
    row: number; col: number; score?: number;
    power?: number; speed?: number; description?: string;
}

/** 矩阵明细行. */
export interface PrismMatrixRow {
    task_id: string;
    uid: string | null;
    material_name: string;
    material_id: string;
    is_local: boolean | null;
    device_model: string;
    /** 平台机型名 (按 XCS 机型码对 device 表), 对不上为空串 */
    device_name: string;
    /** done / failed / processing (源状态归三类) */
    status: string;
    raw_status: string;
    error_message: string;
    /** 失败原因码与中文 (非失败为空串) */
    error_code: string;
    error_label: string;
    created_at: string;
    completed_at: string | null;
    best_position: MatrixBestPosition | null;
    recommended_parameters: { power?: number; speed?: number; pass?: number } | null;
    image_url: string;
    thumb_url: string;
    /** good / bad / 空串 = 无反馈 */
    rating: string;
    n_rows: number | null;
    n_cols: number | null;
    process_type: string;
    material?: MaterialRef | null;
    material_source?: MaterialSource;
}

/** 矩阵汇总. */
export interface PrismMatrixSummary {
    kpi: {
        total: number; done: number; failed: number; processing: number;
        success_rate: number; good: number; bad: number; users: number;
    };
    /** device = XCS 机型码原文; name = 平台机型名 (对不上为空串) */
    by_device: { device: string; name: string; done: number; failed: number; processing: number; total: number; success_rate: number }[];
    /** 失败原因: code = error_message 的 [CODE] 前缀 (另有 ORPHANED / EMPTY / OTHER), label 中文, sample 一条原始错误信息 */
    by_error: { code: string; label: string; count: number; sample: string }[];
    by_material: { material: string; count: number; done: number }[];
}

export interface MatrixListParams {
    device?: string; status?: string; rating?: string; uid?: string; keyword?: string;
    date_from?: string; date_to?: string;
    /** 失败原因码 (by_error[].code / 行的 error_code), 隐含只看失败 */
    error_code?: string;
    page?: number; pageSize?: number;
}

export function listMatrixRecognitions(params: MatrixListParams) {
    return request<PrismPage<PrismMatrixRow>>('/v1/assets/matrix-recognitions', { query: params });
}

export function getMatrixRecognitionStats(params: Omit<MatrixListParams, 'page' | 'pageSize'> = {}) {
    return request<PrismMatrixSummary>('/v1/assets/matrix-recognitions/stats', { query: params });
}

export function getMatrixRecognition(taskId: string) {
    return request<{
        detail: {
            task_id: string; status: string; raw_status: string; uid: string | null;
            device_model: string; device_name: string; material_name: string; material_id: string; is_local: boolean | null;
            error_code: string; error_label: string;
            image_url: string; progress_stage: string; error_message: string;
            created_at: string; completed_at: string | null;
            material?: MaterialRef | null; material_source?: MaterialSource;
            /** 算法 + VLM 原始结果 (结构见 MatrixRecognitionDetail 页注释) */
            result?: Record<string, unknown> | null;
            /** 算法入参快照 (n_rows/n_cols/row_key/col_key/row_title/process_type …), 存量为空 */
            request_input?: Record<string, unknown> | null;
        };
        feedback?: unknown[];
    }>(`/v1/assets/matrix-recognitions/${encodeURIComponent(taskId)}`);
}

/** 读本库时材料加工页实际生效的日期窗口: 未选日期默认最近 30 天 (按所选来源的最新数据日期往前推). */
export interface ProcDateWindow {
    date_from: string;
    date_to: string;
    default: boolean;
    days?: number;
}

export function listProcessMaterials(params: ProcListParams & ProcLocalParams) {
    return request<PrismPage<PrismMaterialRow> & { sentinel_archives?: number; categories?: string[]; date_window?: ProcDateWindow }>(
        '/v1/assets/process-records/materials', { query: params });
}

export function listProcessRecords(params: ProcListParams & ProcLocalParams) {
    return request<PrismPage<PrismArchive & ProcLocalRowExtra> & { categories?: string[]; date_window?: ProcDateWindow }>(
        '/v1/assets/process-records', { query: params });
}

export function getProcessStats(
    params: Pick<ProcListParams, 'device' | 'material_id' | 'date_from' | 'date_to'> & ProcLocalParams & { aligned?: string } = {},
) {
    return request<PrismProcSummary & ProcLocalSummaryExtra>('/v1/assets/process-records/stats', { query: params });
}

/** 同「材料 × 用户」的历次加工 (详情页下半部分). scope=user 只看这个用户, material 放开用户. */
export function listRecordRuns(
    archiveId: number,
    params: { scope?: 'user' | 'material'; page?: number; pageSize?: number } = {},
) {
    return request<PrismPage<PrismArchive> & {
        scope: 'user' | 'material'; material_id: string; user_id: string;
    }>(`/v1/assets/process-records/${archiveId}/runs`, { query: params });
}

export function getProcessRecord(archiveId: number) {
    return request<PrismArchive>(`/v1/assets/process-records/${archiveId}`);
}

/** 人工复核入参: v1 档位配 score/defects, v2 结论配 indicators/unable_reason, 两套不能交叉 (后端 400)。 */
export interface LabelReq {
    quality?: string; score?: number; defects?: string[]; description?: string;
    indicators?: string[]; unable_reason?: string;
}

export function labelProcessRecord(archiveId: number, body: LabelReq) {
    return request<{ ok: boolean }>(`/v1/assets/process-records/${archiveId}/label`, { method: 'POST', body });
}

/** 撤销该归档上的全部人工复核 (prism 侧 status→revoked, 展示退回 AI 判定). */
export function revokeProcessLabel(archiveId: number) {
    return request<{ ok: boolean; revoked: number }>(`/v1/assets/process-records/${archiveId}/label/revoke`, { method: 'POST', body: {} });
}

/** prism 数据源健康状态 (页面顶部提示; 代理模式没有同步状态). */
export function getPrismHealth() {
    return request<{ enabled: boolean; ok?: boolean; error?: string; baseUrl?: string }>(
        '/v1/assets/prism/health');
}


// ============ prism 同步落库 (2026-10-20, doc/数据资产/prism数据同步与材料加工合并-设计-20261020.md) ============
// 材料识别 / 材料加工 / 矩阵识别由直连 prism 改为每小时 :30 增量同步到本库; readLocal=true 后页面读本库,
// 材料加工同时并入历史加工数据 (origin=history). readLocal=false 时页面行为与改造前完全一致.

/** 单个数据集的同步状态行 (asset_sync_state), 与 UgcSyncState 同形. */
export type PrismSyncDataset = UgcSyncState;

export interface PrismSyncState {
    running: boolean;
    failed: boolean;
    /** 各数据集最近一次成功的时刻 (unix 秒) 取最大 */
    lastOkAt: number;
    /** 页面/OpenAPI 是否已改读本库 (数据追平 + 对账完成后才打开) */
    readLocal: boolean;
    /** 每小时第几分跑增量轮 */
    minute: number;
    /** 本库已同步到的最大归档号 */
    archiveMax: number;
    datasets: Partial<Record<'recognition' | 'feedback' | 'archive' | 'label' | 'rating' | 'matrix' | 'rollup', PrismSyncDataset>>;
}

export function getPrismSyncState() {
    return request<PrismSyncState>('/v1/assets/prism-sync/state');
}

/** 手动同步 (full=true 额外做全量对账); 已在跑时后端报「同步进行中, 请稍后」. */
export function triggerPrismSync(full = false) {
    return request<{ started: boolean; full: boolean }>('/v1/assets/prism-sync', {
        method: 'POST', query: full ? { full: '1' } : undefined,
    });
}

/** 数据来源: '' 全部 / studio = prism Studio 实时上报 / history = xcs2.7 历史加工数据. */
export type ProcOrigin = '' | 'studio' | 'history';

/** 历史加工数据并表后的记录 id 号段起点 (≥ 这个数的 archive_id 都是历史行). */
export const HISTORY_RECORD_ID_BASE = 9_000_000_000_000;

/** readLocal 后材料加工新增的筛选 (与 ProcListParams 合用). */
export interface ProcLocalParams {
    origin?: ProcOrigin;
    device_module?: string;
    /** 逗号分隔多值 */
    processing_type?: string;
    processing_mode?: string;
    light_source?: string;
    /** none / ai / manual */
    label_source?: string;
    /** true / false —— 胜出标签置信度 < 0.4 */
    low_conf?: string;
    /** none / evaluable / unevaluable */
    scene_state?: string;
    /** none / photo / restored */
    before_kind?: string;
    sat_reason?: string;
    element_type?: string;
    restore_qc?: string;
    restore_finish?: string;
    restore_label_cat?: string;
    power_min?: string; power_max?: string;
    speed_min?: string; speed_max?: string;
}

/** readLocal 后加工行多出来的字段. */
export interface ProcLocalRowExtra {
    origin?: 'studio' | 'history';
    device_module?: string;
    light_source?: string;
    laser_power_w?: number | null;
    processing_type?: string;
    processing_mode?: string;
    element_count?: number;
    /** photo = 实拍底图 / restored = 模型反推的还原图 (不是实拍) / '' 无 */
    before_kind?: 'photo' | 'restored' | '';
    restore?: { qc: string; finish: string; label_cat: string } | null;
}

export interface ProcOptionItem { value: string; count: number }

export interface ProcOptions {
    device_modules: ProcOptionItem[]; processing_types: ProcOptionItem[]; processing_modes: ProcOptionItem[];
    light_sources: ProcOptionItem[]; element_types: ProcOptionItem[];
    restore_qc: ProcOptionItem[]; restore_finish: ProcOptionItem[]; restore_label_cat: ProcOptionItem[];
    sat_reasons: ProcOptionItem[];
}

export function getProcessOptions(params: Pick<ProcLocalParams, 'origin'> = {}) {
    return request<ProcOptions>('/v1/assets/process-records/options', { query: params });
}

/** readLocal 后统计多出来的键 (原有键形状不变). */
export interface ProcLocalSummaryExtra {
    /** 实际生效的日期窗口 (未选日期默认最近 30 天). */
    date_window?: ProcDateWindow;
    by_origin?: { origin: 'studio' | 'history'; process_count: number }[];
    by_day?: { date: string; studio: number; history: number }[];
    by_device_module?: { device_module: string; process_count: number; labeled: number; success: number; fail: number }[];
    by_processing_type?: { processing_type: string; process_count: number }[];
    satisfaction?: { satisfied: number; unsatisfied: number; unrated: number };
    top_sat_reasons?: { code: string; count: number }[];
    /** 仅 origin != studio 时返回 */
    history?: {
        /** like / dislike = 带反馈的元素里满意 / 不满意条数 (like 新增于 2026-10-21, 老后端没有) */
        element_types: { element_type: string; count: number; like?: number; dislike: number }[];
        restore_qc: { code: string; count: number }[];
        restore_finish: { code: string; count: number }[];
    };
}

/** 历史行详情 (archive_id ≥ HISTORY_RECORD_ID_BASE): 与老的 /v1/assets/process-history/:id 同形. */
export interface ProcHistoryDetail {
    origin: 'history';
    record: ProcessHistoryRow; elements: ProcessHistoryElement[]; material: MaterialRef | null;
    restore: ProcessHistoryRestore | null;
}

export function getProcessHistoryRecord(recordId: number) {
    return request<ProcHistoryDetail>(`/v1/assets/process-records/${recordId}`);
}

// ============ 历史加工数据 (xcs2.7 加工反馈, asset_process_history; 2026-09-07) ============
// 一次性导入 104k 条落库; 结果图已搬到本平台存储 (imageUrl 完整 URL). 数据是静态快照.

export interface ProcessHistoryRow {
    id: number; uuid: string; createdAt: number; updatedAt: number;
    sourceId: string; userUid: string; snCode: string;
    deviceIdRaw: string; deviceCode: string; deviceName: string;
    materialIdRaw: string; materialSource: MaterialSource; materialXcode: string; materialName: string;
    mode: string; processMode: string; laserValues: string;
    elementCount: number; officialCount: number; customCount: number; elementsTruncated: number;
    evaluateType: 'like' | 'dislike' | 'none'; evaluateOptions: string; evaluateDesc: string;
    hasImage: number; imageUrl: string; occurredAt: number; dateStr: string;
    /** 耗材还原 (2026-09-20): 1=未还原 2=已还原; restoredImageUrl 是加工**前**的还原图 */
    restoreStatus: number; restoredImageUrl: string;
    // 详情才有
    elements?: string; processingData?: string; params?: string; extra?: string; evaluateSource?: string;
}

export interface ProcessHistoryElement {
    id: number; recordId: number; seq: number; elementType: string; processingType: string; lightSource: string;
    power: number; speed: number; repeat: number; dpi: number; density: number; bitmapMode: string;
    materialType: string; laserValue: number | null;
}

/** 一条记录的耗材还原: 加工前还原图 + 模型定位 + 质检 + 人工标注 (未还原时接口给 null) */
export interface ProcessHistoryRestore {
    id: number; recordId: number; sourceId: string; batchNo: string;
    restoredImageUrl: string; restoredAttachmentId: number; restoredSha256: string;
    restoreModel: string; restoreBox: string; restoreAreaPct: number; restoreDrift: number;
    /** matte / glossy / mixed —— 中文走 dict restore_finish */
    restoreFinish: string;
    /** ok / minor / fail —— 复核优先级, 不是判废依据; 中文走 dict qc_verdict */
    qcVerdict: string; qcBy: string; qcNote: string; qcAfter: string;
    /** pass / uncertain / reject —— 中文走 dict restore_label */
    labelCat: string; labelSub: string;
    createdAt: number; updatedAt: number;
}

export interface ProcessHistoryParams {
    keyword?: string; device?: string; mode?: string; materialSource?: string; materialXcode?: string;
    evaluate?: string; elementType?: string; hasImage?: string; uid?: string; dateFrom?: string; dateTo?: string;
    /** 还原维度: restored=yes 已还原 / no 仅原始; 其余三个只对已还原的行有意义 */
    restored?: string; qcVerdict?: string; restoreFinish?: string; labelCat?: string;
    /** 按发生时间排序: asc 旧→新; 缺省 / desc 新→旧 */
    order?: 'asc' | 'desc';
    page?: number; pageSize?: number;
}

export interface KV { key: string; name?: string; count: number }

export interface ProcessHistoryStats {
    kpi: {
        total: number; withImage: number; withFeedback: number; like: number; dislike: number;
        official: number; userCustom: number; unknown: number; devices: number; materials: number; users: number;
        /** 已还原 (有加工前还原图) 的记录数 */
        restored: number;
    };
    byDevice: KV[]; byMode: KV[]; byMaterialSource: KV[]; dislikeReasons: KV[]; topMaterials: KV[];
    byElementType: { elementType: string; total: number; like: number; dislike: number }[];
    /** 还原质检 / 表面判定分布 (只统计已还原子集) */
    byQcVerdict: KV[]; byRestoreFinish: KV[];
    byDay: { day: string; count: number; withImage: number; feedback: number }[];
    /** 场景打标分布 (2026-09-15)。三个分母各不相同, 见 SceneCard 的注释 */
    scene: {
        labeled: number;
        evaluable: number;
        unevaluable: number;
        /** 8 类场景, 降序。**一图可多标签**, 之和 > labeled */
        tags: KV[];
        unableReasons: KV[];
    };
}

export interface ProcessHistoryOptions {
    devices: KV[]; modes: KV[]; elementTypes: KV[];
    qcVerdicts: KV[]; restoreFinishes: KV[]; labelCats: KV[];
}

export function listProcessHistory(params: ProcessHistoryParams) {
    return request<{ list: ProcessHistoryRow[]; total: number; page: number; pageSize: number }>('/v1/assets/process-history', { query: params });
}
export function getProcessHistoryStats(params: Omit<ProcessHistoryParams, 'page' | 'pageSize'>) {
    return request<ProcessHistoryStats>('/v1/assets/process-history/stats', { query: params });
}
export function getProcessHistoryOptions() {
    return request<ProcessHistoryOptions>('/v1/assets/process-history/options');
}
export function getProcessHistory(id: string | number) {
    return request<{
        record: ProcessHistoryRow; elements: ProcessHistoryElement[]; material: MaterialRef | null;
        restore: ProcessHistoryRestore | null;
    }>(`/v1/assets/process-history/${id}`);
}

// ---------------- 商品采集中心 (2026-09-08, 亚马逊 Keepa 采集, 采集器 material-collector 直写本库) ----------------
// 字段稀疏且不统一: 采到什么存什么, 缺失为 null; 材质是自由文本, 不关联本平台材料主数据.

export interface ProductImage {
    seq: number;
    filename: string;
    imageUrl: string | null;
    width: number | null;
    height: number | null;
    bytes: number | null;
    status: string;
}

export interface ProductRow {
    asin: string;
    title: string | null;
    brand: string | null;
    model: string | null;
    brandStoreName: string | null;
    brandStoreUrl: string | null;
    rootCategory: number | null;
    categoryTree: string | null;
    categoryPath: string | null;
    manufacturer: string | null;
    partNumber: string | null;
    binding: string | null;
    materials: string | null;
    materialsJson: string | null;
    color: string | null;
    features: string | null;
    ingredients: string | null;
    itemForm: string | null;
    pattern: string | null;
    description: string | null;
    style: string | null;
    includedComponents: string | null;
    size: string | null;
    itemHeightMm: number | null;
    itemLengthMm: number | null;
    itemWidthMm: number | null;
    itemWeightG: number | null;
    thicknessMm: number | null;
    numberOfItems: number | null;
    packageHeightMm: number | null;
    packageLengthMm: number | null;
    packageWidthMm: number | null;
    packageWeightG: number | null;
    unitCount: string | null;
    isHeatSensitive: number | null;
    hazardousMaterials: string | null;
    batteriesRequired: number | null;
    batteriesIncluded: number | null;
    safetyWarning: string | null;
    imageCount: number | null;
    variations: string | null;
    monthlySold: number | null;
    salesRankRef: number | null;
    salesRanks: string | null;
    listedSince: string | null;
    releaseDate: string | null;
    eanList: string | null;
    upcList: string | null;
    gtinList: string | null;
    target: string | null;
    sourceNodeId: number | null;
    bestSellerRank: number | null;
    inScope: string | null;
    fetchStatus: string;
    discoveredAt: string | null;
    fetchedAt: string | null;
    images: ProductImage[];
}

export interface ProductListParams {
    keyword?: string;
    brand?: string;
    target?: string;
    categoryPath?: string;
    material?: string;
    hasMaterial?: string;
    hasImage?: string;
    inScope?: string;
    sort?: string;
    page?: number;
    pageSize?: number;
}

export interface ProductKV { key: string; count: number; withMaterial: number }

export interface ProductStats {
    kpi: {
        total: number; withMaterial: number; withImage: number; brands: number; categoryPaths: number; targets: number;
        scopeMiss: number; withColor: number; withWeight: number;
    };
    images: { files: number; ok: number; small: number; failed: number; uploaded: number; refs: number };
    collect: { nodes: number; lastPulled: string | null; lastFetched: string | null; snapshots: number };
    // generatedAt 这份统计是什么时候算出来的 (Unix 秒)。后端走 stale-while-revalidate:
    // 超过保鲜期会先把上一份原样发回来、同时在后台重算, 所以这个数可能比"现在"旧几小时。
    generatedAt: number;
    byTarget: ProductKV[];
    topBrands: ProductKV[];
    topMaterials: ProductKV[];
    byCategory: ProductKV[];
    byScope: ProductKV[];
    byBinding: ProductKV[];
}

// warming: 后端冷算超 25s 预算时**返回 200 + 四个空数组 + warming=true**, 后台继续算完写缓存
// (assetproduct/handler.go 的 statsBudget 分支; 给 200 是刻意的, 报错会让整页挂掉)。
// 调用方拿到它就该知道"这不是真的没有选项, 是还没算完", 过一会儿再拉一次。
export interface ProductOptions { targets: ProductKV[]; brands: ProductKV[]; categoryPaths: ProductKV[]; materials: ProductKV[]; warming?: boolean }
/** 采集类目候选 (只给类目, 商品打标审核页用; 后端每次直查不缓存, 不会回 warming). */
export interface ProductTargets { targets: ProductKV[]; warming?: boolean }

export interface ProductCategoryNode {
    nodeId: number; target: string; nodeName: string | null; fullPath: string | null; productCount: number | null;
    brandCount: number | null; lastPulledAt: string | null; lastListSize: number | null; lastListUpdate: string | null;
}

export interface ProductSnapshot { nodeId: number; nodeName: string | null; fullPath: string | null; target: string | null; pulledAt: string; bsrRank: number }

export interface ProductDetailResult {
    product: Omit<ProductRow, 'images'>;
    images: ProductImage[];
    sourceNode: ProductCategoryNode | null;
    snapshots: ProductSnapshot[];
    /** 变体里在本库有独立记录的 ASIN (只有被榜单发现过的才有), 只给这些做跳转 */
    knownVariantAsins: string[];
}

export function listProducts(params: ProductListParams) {
    return request<{ list: ProductRow[]; total: number; page: number; pageSize: number }>('/v1/assets/products', { query: params });
}
export function getProductStats(params: Omit<ProductListParams, 'page' | 'pageSize' | 'sort'>) {
    return request<ProductStats>('/v1/assets/products/stats', { query: params });
}
export function getProductOptions() {
    return request<ProductOptions>('/v1/assets/products/options');
}
export function getProductTargets() {
    return request<ProductTargets>('/v1/assets/products/targets');
}
export function getProduct(asin: string) {
    return request<ProductDetailResult>(`/v1/assets/products/${encodeURIComponent(asin)}`);
}

// ---------------- c10s 商家数据中心 (2026-09-09, 直读 customthings-service, 不同步) ----------------
// 一行 = 一个 c10s 定制商品 (source_channel=customthings), 变体 (SKU) 聚合成底图/区域/参数摘要.
// 数据实时来自 c10s 源库, 本平台不落库: 源库不可达时接口报 "c10s 数据源不可用".

/** 可定制区域: 形状 + 宽高 (mm) */
export interface C10sArea {
    shape: string;
    width: number;
    height: number;
    /** 区域左上角坐标 (源侧 printArea.x/y, 与宽高同一坐标系) */
    x: number;
    y: number;
}

/** 材质 (side_designs[].texture.id → t_texture 字典) —— c10s 真正的材质信息 */
export interface C10sTexture {
    id: string;
    title: string;
    image: string;
    showType: string;
    /** 源侧 publish_status: online / offline (下线材质仍可能被存量商品引用) */
    status: string;
}

/** 商品面上的自填材质 (t_product.sides[].material) —— 商家基本不填, 页面不展示 */
export interface C10sMaterial {
    name: string;
    category: string;
    imageUrl: string;
}

export interface C10sMerchant {
    storeId: string;
    /** 展示名: 企业级=企业名, 小商家=店铺名 */
    storeName: string;
    /** 店铺自身名字 (企业分店时与 storeName 不同) */
    shopName: string;
    storeType: string;
    /** enterprise=企业级 / personal=小商家 */
    kind: 'enterprise' | 'personal';
    enterpriseId: string;
    logo: string;
}

export interface C10sProductRow {
    /** 该商品的订单明细条数 (0 = 没被下过单); 只有管理端列表与详情带, 数据集通道不含此字段 */
    orderCount?: number;
    id: string;
    title: string;
    merchant: C10sMerchant;
    thumbnailUrl: string;
    carouselImages: string[];
    /** 底图: 各变体各面的切边设计图 */
    baseImages: string[];
    /** 可设计区域 (去重). 多区域商品来自各面 printAreaArr, 否则来自 printArea */
    areas: C10sArea[];
    /** 源侧 enable_mulit_area: 一个面能不能划多个可设计区域 */
    enableMultiArea: boolean;
    skus: string[];
    skuCount: number;
    hasParam: boolean;
    /** 填了参数的加工类型 (score/engrave/bitmap…) */
    paramModes: string[];
    /** 材质 (来自 t_texture), 去重 */
    textures: C10sTexture[];
    materials: C10sMaterial[];
    sideNames: string[];
    deviceCode: string;
    devices: string[];
    processTech: string;
    publishStatus: string;
    productType: string;
    designMode: string;
    createdAt: number;
    /** 源侧 updated_at, 页面「同步时间」列 (直读无同步动作) */
    updatedAt: number;
}

export interface C10sParamGroup {
    id: string;
    name: string;
    deviceCode: string;
    laserHead: string;
    processTech: string;
    parameters: unknown[];
    /** 参数定位信息 (源侧 processing_position, 形如 {x,y,z,zArr}) */
    processingPosition: { x?: number; y?: number; z?: number; zArr?: number[] };
}

/** 一条加工参数 (源侧 {type, value}); value 是源侧自由结构, 不替它定形状 */
export interface C10sParam {
    type: string;
    value: Record<string, unknown>;
}

export interface C10sSide {
    sideId: string;
    /** 面名 (按 sideId 从商品 sides 里查, 如 front / Back); 查不到为空 */
    sideName: string;
    designImageUrl: string;
    designWidth: number;
    designHeight: number;
    /** 单区域口径 (enableMultiArea=false 时用) */
    printArea: C10sArea | null;
    /** 该面全部可设计区域: 多区域来自 printAreaArr, 单区域就是 printArea 一个 */
    printAreas: C10sArea[];
    machine: string;
    processTech: string;
    processingMode: string;
    /** 该面的加工参数: 一律取自 parameterGroupId 指向的参数组 (面级优先, 退回变体级) */
    parameters: C10sParam[];
    /** 面级绑定的参数组 id (企业店铺); 没绑为空串 */
    parameterGroupId: string;
    /** 参数取自哪个组, 形如 group:<id> */
    parameterSource: string;
    /** 该面的材质 (t_texture); 没选为 null */
    texture: C10sTexture | null;
    /** 该面的完整原文 (详情 key-value 展示用) */
    raw: Record<string, unknown>;
}

export interface C10sVariant {
    id: string;
    sku: string;
    title: string;
    /** 商品本身宽高 mm (源侧 t_product_variant.design_width/height); 源侧填得少, 没值为 0 */
    designWidth: number;
    designHeight: number;
    price: number;
    enable: boolean;
    skuImage: string;
    cover: string[];
    /** 规格值: 颜色 / 大小这类; color 是色值 hex, 可能为空 */
    options: { name: string; val: string; color: string }[];
    /** 变体级参数组 (小商家绑法); 企业店铺为 null, 面级绑定看 sides[].parameterGroupId */
    parameterGroup: C10sParamGroup | null;
    /** 本变体引用到的参数组 (变体级 + 各面), 按 id 索引 —— 面级绑定靠它取组名 */
    paramGroups: Record<string, C10sParamGroup | null>;
    sides: C10sSide[];
}

/**
 * c10s 数据源站点: 美服与国服各有一份 c10s 数据 (两个 MySQL 实例, 表结构一样),
 * 不传 = 美服。国服是否可用看 getC10sHealth().sites。
 */
export type C10sSite = 'us' | 'cn';

/** 站点中文名 (页面 tab 与提示用; 后端 health 也回 label, 这里给前端直接用的一份) */
export const C10S_SITE_LABEL: Record<C10sSite, string> = { us: '美服', cn: '国服' };

export interface C10sListParams {
    site?: C10sSite;
    keyword?: string;
    /** 材质名子串 (匹配 t_texture.title) */
    material?: string;
    /** 材质 id (t_texture.id, 精确) */
    textureId?: string;
    merchantKind?: string;
    enterpriseId?: string;
    hasParam?: string;
    updatedFrom?: string;
    updatedTo?: string;
    page?: number;
    pageSize?: number;
}

export interface C10sStats {
    total: number;
    enterprise: number;
    personal: number;
    withParam: number;
    storeCount: number;
}

export interface C10sOptions {
    enterprises: { enterpriseId: string; name: string; products: number }[];
    /** 材质字典 (t_texture) + 各自被多少商品用到 */
    textures: (C10sTexture & { products: number })[];
}

export function listC10sProducts(params: C10sListParams) {
    return request<{ list: C10sProductRow[]; total: number; page: number; pageSize: number }>(
        '/v1/assets/c10s-products', { query: params });
}
export function getC10sStats(params: Omit<C10sListParams, 'page' | 'pageSize'>) {
    return request<C10sStats>('/v1/assets/c10s-products/stats', { query: params });
}
export function getC10sOptions(site?: C10sSite) {
    return request<C10sOptions>('/v1/assets/c10s-products/options', { query: { site } });
}
export function getC10sProduct(id: string, site?: C10sSite) {
    return request<{ product: C10sProductRow; variants: C10sVariant[] }>(
        `/v1/assets/c10s-products/${encodeURIComponent(id)}`, { query: { site } });
}

/** 单个站点的数据源状态 (enabled=false 表示该站点没配/关了, 页面不给这个 tab) */
export interface C10sSiteHealth {
    site: C10sSite;
    label: string;
    enabled: boolean;
    ok: boolean;
    db?: string;
    error?: string;
}
export function getC10sHealth() {
    return request<{
        enabled: boolean; ok?: boolean; error?: string; db?: string;
        /** 逐站点状态 (美服、国服); 顶层字段是美服那份 */
        sites?: C10sSiteHealth[];
    }>('/v1/assets/c10s/health');
}

// ---------------- 商品打标人工审核 (2026-09-15; 2026-09-23 切 asset_product_label_v2) ----------------
// 一行一套 AI 判定, 人工逐条裁决.
// v2 之前同行并排存着两套判定 (我方判主加工面 / prism 判整图), 页面据此算 agreement
// 做审核分层; v2 合并成一段后那个维度不复存在。2026-09-24 页面改成面向审核员,
// 分层收成一维**审核桶** (见 LabelBucket), 词表映射的细节不再出现在界面上。
// 审核是**对已有行的 UPDATE**: 只写 verifyStatus / final* / remark, AI 的原判定列一律不动 ——
// 两套判定要长期留着做模型对比, 被人工覆盖就失去基线了.
//
// 两个数据源各一个 tab (2026-09-16 加的 c10s): 亚马逊采集是**图**粒度, c10s 是 **SKU** 粒度,
// 展示字段也各有各的 —— 所以 list / stats 的 source **必填**, 没有"两个源合起来"这个口径.

/** 打标数据源 = 页面的 tab. 后端 source 必填, 传错或不传都是 400, 不会静默给某一个源 */
export type LabelSource = 'product_collect' | 'c10s_variant';

/**
 * 人工裁决结果: 未审 / 认可 / 已修正 / 无法人工判断.
 *
 * disputed 的界面文案是「无法人工判断」(图看不清 / 商品本身看不出材质), 存储值保持不变.
 * 它不能并进 confirmed —— 否则分不出"人确认了 AI 判对"和"人根本看不出来",
 * 而后者正是回头调置信度阈值时必须先摘掉的样本.
 */
export type VerifyStatus = 'pending' | 'confirmed' | 'corrected' | 'disputed';

/**
 * 审核桶 = 审核员眼里的四个去处, **互斥且穷尽**, 所以进度条的分布和翻页结果永远对得上.
 *
 * - pending   待审
 * - reviewed  已审核 (= 库里的 confirmed + corrected)
 * - disputed  无法人工判定 (人看过, 判不了; 终态, 不进下游)
 * - excluded  已剔除 (主加工面在图上认不出来), **只读**, 仅供查看
 *
 * 2026-09-24 从"核验状态 4 档 × 映射落地 3 档"两维收成这一维。撤掉映射那维是因为
 * exact/mapped/unmapped 描述的是词表映射的实现细节, 审核员看不懂也用不上;
 * 合并 confirmed/corrected 同理 —— 对他们而言两者都是"这条审过了"。
 * **两个区分在库里原样保留**, 只是不再摆到页面上。
 */
export type LabelBucket = 'pending' | 'reviewed' | 'disputed' | 'excluded';

/**
 * AI 判定 (v2 起只有一段).
 *
 * l1Raw / l2Raw 是模型输出的**半开放原值**, 未经同义词映射。
 * (2026-09-24 前还有一组同样带 "raw" 的 rawMaterials/rawColor —— 亚马逊采集原文,
 * 含义完全不同、极易混淆, 已随审核页改版撤掉。)
 */
export interface ProductLabelAI {
    /** 官方值 (经同义词映射). l2 为空 = 落不到官方词表, 此时 l2Raw 仍有值 */
    l1: string;
    l2: string;
    /** 半开放原值. 官方值为空时, 它是判断"AI 到底看出了什么"的唯一线索 */
    l1Raw: string;
    l2Raw: string;
    /** AI 从图上推的 hex, 页面只做色块展示。**颜色只有这一个出参** ——
     *  原先还有个 11 色枚举 color, 2026-09-25 退场: 它不是官方词表(出自产品需求文档的
     *  列举), 页面从来没显示过, 而下游绑定一直按 colorHex 查库比色 */
    colorHex: string;
    materialName: string;
    /** 译文, 由 materialName 译得的派生值(打标侧 cli_translate_name.py 写)。
     *  英文原文一并下发: 页面中文为主、英文小字跟后, 排查时页面上就看得到落库的英文值 */
    materialNameZh: string;
    surfaceProcess: string;
    /** 可空: 0 与「没跑出置信度」是两回事 (后端是 *float64), 页面必须判 null 再 toFixed */
    confidence: number | null;
}

export interface ProductLabelRow {
    id: number;
    /** product_collect = image_file.filename (图粒度, 不是 ASIN 粒度); c10s_variant = SKU 的 variant_id */
    sourceId: string;
    imageUrl: string;
    title: string;
    brand: string;
    /** 以下两个仅 product_collect 有值, c10s 行上是空串 */
    asin: string;
    /** 采集类目 (payload.target) */
    target: string;

    // 以下五个仅 c10s_variant 有值, 亚马逊行上是空串.
    sku: string;
    productId: string;
    /** 加工工艺 laser / print (c10s 自己的词表, 走 c10sLabels.processTechText) */
    processTech: string;
    /** base_image = 回退到商品级图 (同商品各 SKU 共用一张) / sku_image = SKU 自己的图.
     *  商品级图上判出来的结论对单个 SKU 不一定成立, 裁决时要看得见 */
    imageSource: string;
    /** t_texture 材质线索的 JSON 数组**原文** (无值时空串), 用 parseJSON 解析.
     *  未参与判定, 仅供人工对照 —— c10s 实测只有 7.4% 的 SKU 有值 */
    rawTextures: string;

    ai: ProductLabelAI;
    /** 库里的原始状态 (confirmed / corrected 在页面上都显示成「已审核」).
     *  留着它是因为"再按一次即改判"要知道从哪个状态改过来 */
    verifyStatus: VerifyStatus;
    /** 人工修正值. 三项各自独立, 空 = 这一项没改, 沿用 AI 的 */
    finalL1: string;
    finalL2: string;
    finalMaterialName: string;
    finalColorHex: string;
    remark: string;
    /** 主加工面在图上认不出来 ⇒ 本行**只读**, 仅供查看.
     *  excludeReason 是原因码 (other_face_only / side_view_only / …), 页面翻成人话 */
    excluded: boolean;
    excludeReason: string;
}

export interface ProductLabelListParams {
    /** **必填**. 后端没有"两个源合起来"的口径, 传空 = buildQuery 丢弃 = 400.
     *  定成必填就是为了把这个错提前到编译期 */
    source: LabelSource;
    /** 空 = 全部, 但**不含已剔除** —— 那批被剔出审核面正是为了别占视线,
     *  混进"全部"就等于没剔。要看它们得显式选 excluded 这个桶 */
    bucket?: LabelBucket | '';
    /** 采集类目, **亚马逊专有** —— 传给 c10s 后端直接 400 (它的 payload 没有这个键,
     *  不拦就是干干净净筛出 0 条, 和"这批审完了"分不开) */
    target?: string;
    /** 加工工艺 laser / print, **c10s 专有** —— 同上, 传给亚马逊 400.
     *  没有"未知工艺"这一档: 源侧缺 process_tech 的 40 行走"全部"才翻得出来 */
    processTech?: string;
    /** 商品名模糊 (payload.title) */
    keyword?: string;
    /** 官方一级(映射后), 精确 */
    l1?: string;
    /** 半开放一级原值, 精确 */
    rawL1?: string;
    /** 抽审：取当前口径里 `CRC32(id‖seed)` **最小的 sampleSize 行**。
     *
     *  **两个必须同时给**，后端只给一半会 400 —— 静默退回全量的话，审核员以为在抽审、
     *  实际审的是 id 最小的那几百条，而 id 的品类分布极不均（实测 watch 在低 id 区
     *  密度 83%），偏差在页面上完全看不出来。
     *
     *  同一组 (筛选, seed, size) 永远圈出同一批行、与审核进度无关，所以事后能原样
     *  重算样本集来算准确率。 */
    sampleSeed?: string;
    sampleSize?: number;
    page?: number;
    pageSize?: number;
    /** 翻批游标：取 id 比它大的下一批，**给了就不看 page**。
     *
     *  待审队列是审一条少一条的，offset 的前提（结果集不动）不成立：审完 k 条再按
     *  page+1 取，offset 指到的已经是原来的第 pageSize+k 条，中间 k 条没审的被跳过。
     *  钉在 id 上就没这个问题 —— 审过的行退出结果集不影响 `id > afterId` 的判定。 */
    afterId?: number;
}

/** 顶部进度条用. 不变式: pending + reviewed + disputed = total,
 *  且 excluded 与它们**无交集** —— 它统计的正是被 total 排除掉的那批 */
export interface ProductLabelStats {
    /** 审核面总数, **不含已剔除**: 剔除行永远不会被审, 算进分母会让进度条走不满。
     *  页面「全部」那一档的计数直接用它 —— 那一档列的就是同一批行, 另算一个数迟早对不上 */
    total: number;
    pending: number;
    /** 已审核 = 库里的 confirmed + corrected 合并后的数, 且不含已剔除 ——
     *  页面标成「已审核(可用)」, 因为这一桶正是真正能进下游的那批 */
    reviewed: number;
    disputed: number;
    /** 已剔除. 页面必须显示它: 否则队列凭空少一半, 审核员只会以为数据丢了 */
    excluded: number;
    /** 可打标的商品图总数 (来自采集表 image_file, 与打标侧的待办准入同口径)。
     *  **不在上面那条不变式里, 也不来自结果表** —— 它是"一共有多少张图可以打",
     *  而 total 是"打完之后有多少张要审"; 两者之差就是还没打标的量。
     *  c10s 是实时代理数据集, 本平台没有它的图全集, 那边恒 0, 页面据此不显示这一段。 */
    imageTotal: number;
}

export interface ProductLabelVerifyReq {
    verifyStatus: VerifyStatus;
    /** corrected 时必填且必须落在 vocab 词表内; 其余状态后端一律清空, 不必传 */
    finalL1?: string;
    finalL2?: string;
    /** 人工改过的耗材名与色值。都可留空 = 这一项没改, 沿用 AI 的。
     *  finalColorHex 必须是 #RRGGBB —— 后端按这个正则拦, 因为下游要拿它跟 prism
     *  那套 ΔE 比色, 格式放宽会让解析失败的行静默落进"匹配不上任何耗材" */
    finalMaterialName?: string;
    finalColorHex?: string;
    remark?: string;
}

/** 下拉词表: 后端实时读 material_category 两级树, 不在前端硬编码.
 *  2026-09-24 起不再含颜色枚举 —— 人工改颜色改的是 color_hex, 那套 11 色本就不是官方词表。
 *
 *  **中英成对下发**: 页面显示中文, 但**提交与存储一律英文** —— 下游拿 finalCategory* 去对
 *  材料主数据, 而 material.type1/type2 存的是英文。前端自己维护一张中→英反查表的话,
 *  那张表迟早与 material_category 漂移。 */
export interface ProductLabelVocab {
    taxonomy: { l1: string; l1Zh: string; l2: { en: string; zh: string }[] }[];
}

export function listProductLabels(params: ProductLabelListParams) {
    return request<{ list: ProductLabelRow[]; total: number; page: number; pageSize: number }>(
        '/v1/assets/product-labels', { query: params });
}

/** 只吃 source, 不吃筛选: 后端这个端点是**单个源的全量口径**, 传筛选也不生效 ——
 *  留个能传的入参只会让调用方以为拿到的是"当前筛选的进度". 要按筛选计数就用 list 的 total.
 *
 *  source 是例外, 因为它不是筛选而是口径本身: 两个源粒度不同 (图 vs SKU),
 *  合起来的"已审 N/M"不对应任何一件真实的事. 切 tab 必须重新拉一次. */
/** 一个采集类目的审核进展。
 *
 *  **两套口径, 字段名把它写死**: `reviewable` / `excludedAll` 是**全量**(这个品类一共
 *  多少要审、被剔了多少), 其余四个是**样本内**(本轮抽审的进度)。
 *  2026-10-10 之前没分开, 页面上「已审 2,114」和抽检页「待审 310」对不上, 没人看得懂。 */
export interface ProductLabelCategoryRow {
    target: string;
    /** 打标进度的**分母**: 这个类目清洗后一共多少张图可以打标。
     *  与打标队列的准入口径逐字一致（清洗通过 + 下载成功 + 有 url）。
     *  已打标 = reviewable + excludedAll，占比 = 已打标 / cleanImages。
     *  **一张图都没打的类目也会在列表里**（其余字段全 0）—— 否则整个类目从页面消失，
     *  而「消失」和「已经打完了」长得一模一样。c10s 源没有图全集，这里恒为 0。 */
    cleanImages: number;
    /** 全量: 审核面总数(不含已剔除) */
    reviewable: number;
    /** 全量: 被剔除的条数 */
    excludedAll: number;
    /** **全量**人工审核进展，与下面样本内的 confirmed/corrected/disputed 是两套数。
     *  抽样只是派活用的，人实际审过的往往远多于样本 —— 实测生产 earphone 全量审了
     *  4,444 条（整类普查完了），样本内只有三百条。只看样本内会把已经做完的人工量
     *  当成没做过。reviewedAll = 确认 + 修正；disputedAll = 判不了。 */
    reviewedAll: number;
    disputedAll: number;
    /** 本轮样本量 = min(300, reviewable) */
    sampleN: number;
    /** 以下四项都是**样本内**的 */
    pending: number;
    confirmed: number;
    corrected: number;
    disputed: number;
}

/** 按采集类目的审核进展。**不吃筛选, 只吃 source** —— 这一页就是全量口径。 */
/** 按采集类目的审核进展。
 *
 *  ⚠️ **抽样参数必须传**, 而且要和「人工抽检」页用的完全一致 —— 后端收不到它们就会走
 *  不抽样分支、返回全量口径, 于是统计页的"已审"和抽检页的"待审"来自两个不同的总体,
 *  加起来会超过样本量。2026-10-10 踩过: 耳机统计页显示已审 269(全量), 抽检页待审
 *  189(样本内), 没人看得懂。这种错**没有任何报错**, 两边都是合法数字。 */
export function getProductLabelCategoryStats(
    source: LabelSource, sample: { sampleSeed: string; sampleSize: number },
) {
    return request<{ list: ProductLabelCategoryRow[] }>(
        '/v1/assets/product-labels/category-stats', { query: { source, ...sample } });
}

/** 顶部进度条。**吃与列表同一套筛选和抽样** —— 两个数必须落在同一批行上，否则抽审时
 *  会出现「待审 47,698 / 列表 310 条」这种自相矛盾的画面，审核员会以为自己有 4.7 万条
 *  要做，而实际任务是抽出来的那几百条。
 *
 *  `source` 仍是必填：它不是筛选而是口径本身，两个源粒度不同（图 vs SKU），
 *  合起来的「已审 N/M」不对应任何一件真实的事。 */
export function getProductLabelStats(params: ProductLabelListParams) {
    return request<ProductLabelStats>('/v1/assets/product-labels/stats', { query: params });
}

export function verifyProductLabel(id: number, body: ProductLabelVerifyReq) {
    return request<{ id: number; verifyStatus: VerifyStatus }>(
        `/v1/assets/product-labels/${id}/verify`, { method: 'POST', body });
}

export function getProductLabelVocab() {
    return request<ProductLabelVocab>('/v1/assets/product-labels/vocab');
}

// ---------------- c10s 订单明细 (2026-09-15) ----------------
// 一行 = 订单里的一行商品 (源侧 t_order_item), 按 product_id 挂在商品下面: 一个商品可以有上万条
// (美服生产头部 12,829 条), 所以一律分页。
// 图在**面上** sides[].designFileImageUrl (买家定制后的设计图) —— 源侧顶层的 design_file_image_url
// 与 designFileImageThumbnailUrl 实测全空, 别用; 列表已把各面图去重成 images 给页面直接用。

/** 加工状态: 待加工 / 已加工 (源侧 process_status 只有这两个值) */
export type C10sOrderStatus = 'pending' | 'completed';

/** 订单明细的一个面: 买家定制结果 + 下单时的加工参数快照 */
export interface C10sOrderSide {
    sideId: string;
    sideName: string;
    /** 买家定制后的设计图 (订单图墙的图源) */
    designFileImageUrl: string;
    designElementImageUrl?: string;
    designElementImageSvgUrl?: string;
    designColor?: string;
    machine: string;
    processTech?: string;
    processingMode?: string;
    texture: C10sTexture | null;
    /**
     * 下单时的加工参数快照 (空壳已被后端过滤)。⚠ 与商品侧的参数不是一回事:
     * 商品侧一律从参数组取, 这里是买家下单当时写进明细的值。只有单条详情接口带。
     */
    parameters?: C10sParam[];
    /** 设计参数原文 (源侧自由结构); 只有单条详情接口带 */
    designParams?: unknown;
    /** 加工定位 {x,y,z}, 全 0 时后端不给; 只有单条详情接口带 */
    processingPosition?: { x?: number; y?: number; z?: number };
}

export interface C10sOrderRow {
    id: string;
    /** 所属订单 id (同一个订单可以有多行明细) */
    orderId: string;
    productId: string;
    productVariantId: string;
    sku: string;
    /** 下单时的商品标题快照 (商品后来改名也不动) */
    productTitle: string;
    quantity: number;
    /** 下单时的单价快照 */
    price: number;
    processStatus: C10sOrderStatus;
    processTech: string;
    productType: string;
    deviceCode: string;
    supportDevices: string[];
    /** 外站订单号 (空 = 非 Etsy 单) */
    etsyTransactionId: string;
    consumedStock: number;
    sides: C10sOrderSide[];
    /** 各面设计图去重 (图墙直接用) */
    images: string[];
    /** 商品**当前**标题 (与 productTitle 不一致 = 商品改过名); 单条详情接口不带 */
    curProductTitle?: string;
    /** 商家 (单条详情接口不带) */
    merchant?: C10sMerchant;
    createdAt: number;
    updatedAt: number;
}

/** 某商品的订单概览 (详情页统计条) */
export interface C10sOrderStats {
    /** 明细条数 */
    items: number;
    /** 件数 (SUM(quantity)) */
    quantity: number;
    completed: number;
    pending: number;
    /** 去重后的订单数 */
    orders: number;
    /** 首单 / 末单时间 (unix 秒, 0 = 无单) */
    firstAt: number;
    lastAt: number;
}

/**
 * 订单 tab 的筛选: 订单侧 (productId / 状态 / 有无图 / 下单时间) + 商品与商家侧
 * (关键字 / 材质 / 商家类型 / 企业 / 商品更新时间窗, 与商品 tab 同名同义)。
 */
export interface C10sOrderListParams {
    site?: C10sSite;
    /** 只看某个商品的订单 (商品列表/详情的「订单 N」入口就是带上它跳过来) */
    productId?: string;
    status?: C10sOrderStatus | '';
    /** 只看有/没有设计图 */
    hasImage?: 'yes' | 'no' | '';
    /** 下单时间窗 YYYY-MM-DD (含当日) */
    orderedFrom?: string;
    orderedTo?: string;
    /** 以下与商品 tab 同名: 匹配的是所属商品/商家 */
    keyword?: string;
    material?: string;
    textureId?: string;
    merchantKind?: string;
    enterpriseId?: string;
    page?: number;
    pageSize?: number;
    /** 顺序翻页锚点 (上一页返回的 nextBefore): 带上就不吃 OFFSET, 深翻更快 */
    before?: string;
}

export function listC10sOrders(params: C10sOrderListParams) {
    return request<{
        list: C10sOrderRow[]; total: number; page: number; pageSize: number;
        /** 下一页锚点; 本页不满 (到底了) 时不给 */
        nextBefore?: string;
    }>('/v1/assets/c10s-orders', { query: params });
}

export function getC10sOrderStats(params: Omit<C10sOrderListParams, 'page' | 'pageSize' | 'before'>) {
    return request<C10sOrderStats>('/v1/assets/c10s-orders/stats', { query: params });
}

/** 单条明细 (含下单参数快照与设计参数) —— 图墙点开一条时才调 */
export function getC10sOrder(id: string, site?: C10sSite) {
    return request<C10sOrderRow>(`/v1/assets/c10s-orders/${encodeURIComponent(id)}`, { query: { site } });
}

// ---------------- 商品打标关联官方耗材 (2026-10-08) ----------------
//
// 一张已审商品图 → 一个官方耗材。判定在平台外跑批 (material-asset-labeler),
// 平台负责人工审核与按品类放行。审核页看的是「这个耗材和图上的材质是不是等价、
// 参数能不能复用」, 不是「这张图是不是这个耗材的商品图」。

/** 五档互斥。blocked 不进人工队列 —— 它等的是工艺侧补 color_hex, 人看也解决不了 */
export type BindStatus = 'auto' | 'confirmed' | 'rejected' | 'review' | 'blocked';

/** 一个官方耗材候选 (判定结果或备选) */
export interface MaterialCand {
    xcode: string;
    nameZh: string;
    colorHex: string;
    image: string;
    thickness: number | null;
    /** 与商品图主加工面的色差 ΔE00; 算不出时为 null (缺 color_hex) */
    deltaE: number | null;
    /** 加权总分 0.4·颜色 + 0.6·材料名语义 */
    totalScore: number | null;
    /** 库里查不到这个 xcode = 死链 (材料被删)。页面要标出来, 不能静默当没有 */
    gone: boolean;
}

export interface ProductMaterialRow {
    id: number;
    labelId: number;
    bindStatus: BindStatus;
    /** 没绑上的原因码 (prism gate_reason) 或等补齐的说明 */
    reason: string;
    /** 打标侧分类 L1/L2, 放行以它为单位 */
    batch: string;
    remark: string;
    updatedAt: number;
    updatedBy: number;
    /** ΔE>20 或总分<0.75 —— 审核优先看这批 */
    highRisk: boolean;
    /** 人工换过选 (xcode 与 auto_xcode 不同), 也是「跑批判错」的判据 */
    switched: boolean;

    // 商品侧 (只读)
    sourceId: string;
    imageUrl: string;
    title: string;
    auditedL1: string;
    auditedL2: string;
    materialName: string;
    materialNameZh: string;
    colorHex: string;
    surfaceProcess: string;

    // 耗材侧
    /** 当前生效的; 没绑上时为 null */
    chosen: MaterialCand | null;
    /** 跑批原判; 只在人工换选过时才给, 否则与 chosen 同一个 */
    auto: MaterialCand | null;
    /** 同样过门的备选, 供换选 */
    alternatives: MaterialCand[];
}

/** 一个品类的审核进度。accuracy 现算 (correct/audited), 没审过为 null */
export interface ProductMaterialBatchStat {
    batch: string;
    total: number;
    bound: number;
    review: number;
    blocked: number;
    audited: number;
    correct: number;
    highRisk: number;
    released: number;
    accuracy: number | null;
    /** 已绑定行的 ΔE 中位数; 该品类一条都没绑上时为 null。
     *  它比命中率更能说明问题: 命中率高但中位数也高 = 颜色差得明显、全靠材料名撑过门 */
    deMedian: number | null;
    /** 整类跳过的说明; 非 blocked 品类为空串 */
    blockReason: string;
}

export interface ProductMaterialListParams {
    batch?: string;
    bindStatus?: BindStatus;
    /** high / low; 空=不筛 */
    risk?: 'high' | 'low';
    page?: number;
    pageSize?: number;
}

export function listProductMaterials(params: ProductMaterialListParams) {
    return request<{ list: ProductMaterialRow[]; total: number; page: number; pageSize: number }>(
        '/v1/assets/product-materials', { query: params });
}

export function getProductMaterialStats() {
    return request<{ batches: ProductMaterialBatchStat[] }>('/v1/assets/product-materials/stats');
}

/** 逐条裁决。换选也走 confirm, 另传 xcode (必须是跑批原判或其备选之一) */
export function verdictProductMaterial(
    id: number,
    body: { action: 'confirm' | 'reject'; xcode?: string; remark?: string },
) {
    return request<{ id: number; bindStatus: BindStatus }>(
        `/v1/assets/product-materials/${id}/verdict`, { method: 'POST', body });
}

export interface ProductMaterialRelease {
    batch: string;
    released: number;
    /** 放行时的抽审快照; 页面上的实时数用 stats 的 audited */
    sampled: number;
    correct: number;
    note: string;
    releasedAt: number;
    releasedBy: number;
}

export function listProductMaterialReleases() {
    return request<{ list: ProductMaterialRelease[] }>('/v1/assets/product-materials/releases');
}

/** 放行或撤回。准确率低于 90% 或抽审少于 30 条时后端拒绝, 传 force 可越过 (会记进 note) */
export function releaseProductMaterial(body: {
    batch: string; released: 0 | 1; force?: boolean; note?: string;
}) {
    return request<{ batch: string; released: number; sampled: number; correct: number }>(
        '/v1/assets/product-materials/releases', { method: 'POST', body });
}

// ---- 满意度统计 (2026-10-23): 官方材料 × 设备 (机型 × 激光模组), 两套口径 ----

/** 满意度计数 (按所选口径: adopted 严格 = 采纳推荐参数 / all 不限参数). */
export interface SatCounts {
    process_count: number;
    satisfied: number;
    unsatisfied: number;
    /** 有加工但未评价 (不进分母) */
    unrated: number;
    /** 样本量 = 满意 + 不满意 */
    rated: number;
    /** 0~1; 无评价为 null */
    satisfaction_rate: number | null;
}

export type SatScope = 'all' | 'adopted';

export interface SatDeviceRow extends SatCounts {
    device_name: string;
    device_module: string;
    /** 激光模组 (瓦数/光源, 如 "blue 20W"); 取不到为空 */
    module: string;
}

export interface MaterialSatisfactionRow extends SatCounts {
    xcode: string;
    material_name: string;
    type1: string;
    devices: SatDeviceRow[];
}

export interface SatComboRow extends SatDeviceRow {
    xcode: string;
    material_name: string;
    type1: string;
}

export interface SatGroupRow extends SatCounts {
    key: string;
    label: string;
    /** 设备 × 模组 时为模组段 */
    sub: string;
    materials: number;
}

export interface SatOverview {
    summary: {
        process_count: number; materials: number; devices: number; device_modules: number; combos: number;
        adopted: SatCounts; all: SatCounts;
    };
    composition: (SatCounts & { kind: 'official' | 'legacy_missing' | 'user_custom' | 'none' })[];
    top_materials: (SatCounts & { xcode: string; material_name: string; type1: string; devices: number })[];
    top_combos: SatComboRow[];
    by_device: SatGroupRow[];
    by_device_module: SatGroupRow[];
    by_type1: SatGroupRow[];
    low_materials: SatComboRow[];
    worst_combos: SatComboRow[];
    reasons: { code: string; count: number }[];
    monthly: (SatCounts & { month: string; total: number })[];
    worst_min: number;
    options: { device_names: { value: string; process_count: number }[]; device_modules: { value: string; process_count: number }[] };
    categories: string[];
    generated_at: number;
}

export interface MaterialSatisfactionParams {
    scope?: SatScope;
    keyword?: string;
    type1?: string;
    device_name?: string;
    device_module?: string;
    min_rated?: number;
    /** 统计日期 YYYY-MM-DD (UTC, 含首尾两天); 不给 = 全部数据 */
    date_from?: string;
    date_to?: string;
    sort?: 'rated' | 'rate' | 'process_count';
    order?: 'asc' | 'desc';
    page?: number;
    pageSize?: number;
}

export function listMaterialSatisfaction(params: MaterialSatisfactionParams) {
    return request<{ data: MaterialSatisfactionRow[]; total: number; page: number; page_size: number }>(
        '/v1/assets/material-satisfaction', { query: params });
}

export function getMaterialSatisfactionOverview(
    params: Omit<MaterialSatisfactionParams, 'sort' | 'order' | 'page' | 'pageSize'> & { worst_min?: number },
) {
    return request<SatOverview>('/v1/assets/material-satisfaction/overview', { query: params });
}
